import styled, { css } from 'styled-components';
import Text, { TEXT_VARIANT_BODY_MEDIUM } from '@components/1-atoms/Text';
import SpriteSVG from '@app/components/1-atoms/SpriteSVG';
import {
  MESSAGE_BOX_VARIANT_ERROR,
  MESSAGE_BOX_VARIANT_MESSAGE,
  MESSAGE_BOX_VARIANT_WARNING,
  MESSAGE_BOX_VARIANT_SNACKBAR,
  MESSAGE_BOX_VARIANT_SNACKBAR_ERROR,
} from './MessageBox';
import { flexCenterVertical, when } from '@stylesheets/helpers';

export const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  position: relative;

  ${({
    $withBorderRadius,
    $withEndingIconButton,
    theme: { borderRadius, spacing },
  }) => css`
    gap: ${spacing.level2};
    padding-left: ${spacing.level5};
    padding-right: ${spacing.level3};

    ${when(
      $withBorderRadius,
      `
        border-radius: ${borderRadius?.small};
      `,
    )}

    ${when(
      $withEndingIconButton,
      `
        padding-right: 0;
      `,
    )}
  `}
`;

export const Content = styled.div`
  display: flex;
  padding: var(--Level-4, 12px) 0px;

  ${({ $withEndingIconButton, theme: { spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level4} 0px;

    ${when(
      $withEndingIconButton,
      `
        max-width: 72%;
      `,
    )}
  `}
`;

export const StateLayer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0.16;

  ${({ $variant, theme: { colors } }) => css`
    ${when(
      [MESSAGE_BOX_VARIANT_ERROR].includes($variant),
      `
        background-color: ${colors?.error};
      `,
    )}

    ${when(
      $variant === MESSAGE_BOX_VARIANT_MESSAGE,
      `
        background-color: ${colors?.primary};
      `,
    )}

    ${when(
      $variant === MESSAGE_BOX_VARIANT_WARNING,
      `
        background-color: ${colors?.caution};
      `,
    )}

    ${when(
      [
        MESSAGE_BOX_VARIANT_SNACKBAR,
        MESSAGE_BOX_VARIANT_SNACKBAR_ERROR,
      ].includes($variant),
      `
        background-color: ${colors?.surfaceVariant};
        opacity: 1;
        z-index: -1;
      `,
    )}
  `}
`;

export const Icon = styled(SpriteSVG)`
  width: 24px;
  height: 24px;
  align-items: flex-start;
  flex-shrink: 0;

  ${({ $variant, theme: { colors } }) => css`
    fill: ${colors?.onSurfaceVariant};

    ${when(
      [MESSAGE_BOX_VARIANT_ERROR, MESSAGE_BOX_VARIANT_SNACKBAR_ERROR].includes(
        $variant,
      ),
      `
        fill: ${colors?.error};
      `,
    )}

    ${when(
      $variant === MESSAGE_BOX_VARIANT_MESSAGE,
      `
        fill: ${colors?.primary};
      `,
    )}

    ${when(
      $variant === MESSAGE_BOX_VARIANT_WARNING,
      `
        fill: ${colors?.caution};
      `,
    )}

    ${when(
      $variant === MESSAGE_BOX_VARIANT_SNACKBAR,
      `
        fill: ${colors?.inverseSurfaceVariant};
      `,
    )}
  `}
`;

/**
 * Wrapper for caller-provided React-node icons (e.g. react-icons).
 * Mirrors the {@link Icon} sprite component's size and per-variant colour
 * so a hand-rolled icon visually matches the rest of the system.
 */
export const LeadingIconSlot = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & > svg {
    width: 20px;
    height: 20px;
  }

  ${({ $variant, theme: { colors } }) => css`
    color: ${colors?.onSurfaceVariant};

    ${when(
      [MESSAGE_BOX_VARIANT_ERROR, MESSAGE_BOX_VARIANT_SNACKBAR_ERROR].includes(
        $variant,
      ),
      `
        color: ${colors?.error};
      `,
    )}

    ${when(
      $variant === MESSAGE_BOX_VARIANT_MESSAGE,
      `
        color: ${colors?.primary};
      `,
    )}

    ${when(
      $variant === MESSAGE_BOX_VARIANT_WARNING,
      `
        color: ${colors?.caution};
      `,
    )}

    ${when(
      $variant === MESSAGE_BOX_VARIANT_SNACKBAR,
      `
        color: ${colors?.inverseSurfaceVariant};
      `,
    )}
  `}
`;

export const Label = styled(Text).attrs(({ $variant: textVariant }) => {
  const variant = TEXT_VARIANT_BODY_MEDIUM;
  let color = 'onSecondaryContainer';

  if (
    [MESSAGE_BOX_VARIANT_ERROR, MESSAGE_BOX_VARIANT_SNACKBAR_ERROR].includes(
      textVariant,
    )
  ) {
    color = 'onErrorContainer';
  }

  if (textVariant === MESSAGE_BOX_VARIANT_WARNING) {
    color = 'onCautionContainer';
  }

  if (textVariant === MESSAGE_BOX_VARIANT_SNACKBAR) {
    color = 'inverseSurfaceVariant';
  }

  return {
    variant,
    color,
  };
})`
  place-self: center;
  white-space: break-spaces;
`;

export const EndingControls = styled.div`
  ${flexCenterVertical}
  margin-left: auto;

  ${({ theme: { spacing } }) => css`
    min-height: ${spacing.level9};
  `}
`;
